import { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import { Alert, Button } from "@mui/material";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import { API } from "aws-amplify";
import { AuthState } from "@aws-amplify/ui-components";
import Main from "../../components/Layout/Main/Main";
import Sidebar from "../../components/Layout/Sidebar/Sidebar";
import ProfileCard from "../../components/ProfileCard/ProfileCard";
import { buildAlumPath } from "../../constants/routes";
import {
  listPublicAlumniProfiles,
  listAlumniProfiles,
} from "../../graphql/custom/customQueries";
import AlumniSearch from "../../components/AlumniSearch/AlumniSearch";
import AlumniFilter from "../../components/AlumniFilter/AlumniFilter";
import { branches } from "../../constants/enums";
import { useAuth } from "../../hooks/useAuth";
import "./alumni.scss";

const propTypes = {
  cohorts: PropTypes.objectOf(PropTypes.any).isRequired,
};

const batchSize = 12;

const Alumni = ({ cohorts }) => {
  const [allAlumni, setAllAlumni] = useState([]);
  const [alumniToDisplay, setAlumniToDisplay] = useState([]);
  const [batchEndIndex, setBatchEndIndex] = useState(batchSize);
  const [errorMsg, setErrorMsg] = useState("");
  const auth = useAuth();

  useEffect(() => {
    const fetchAndSetAllAlumni = async () => {
      try {
        const showAllProfiles = auth.userGroups.current.some(
          (group) => group === "admin" || group === "alumni"
        );
        const fetchedData = await API.graphql({
          query: showAllProfiles
            ? listAlumniProfiles
            : listPublicAlumniProfiles,
        });
        const alumni = showAllProfiles
          ? fetchedData?.data?.listAlumniProfiles?.items
          : fetchedData?.data?.publicAlumniProfiles?.items;
        if (alumni?.length > 0) {
          setAllAlumni(alumni);
          setAlumniToDisplay(alumni);
          setErrorMsg("");
        } else {
          setErrorMsg("No alumni data returned from database.");
        }
      } catch (e) {
        setErrorMsg("Failed to get alumni data. Please try again later.");
      }
    };

    if (auth.authState === AuthState.SignedIn) {
      fetchAndSetAllAlumni();
    }
  }, [auth.authState]);

  const updateBatchEndIndex = () => {
    const nextEndIndex = batchEndIndex + batchSize;
    if (nextEndIndex > alumniToDisplay.length) {
      setBatchEndIndex(alumniToDisplay.length);
    } else {
      setBatchEndIndex(nextEndIndex);
    }
  };

  const alumCards = alumniToDisplay.slice(0, batchEndIndex).map((alum) => {
    const cohort = cohorts[alum.cohortID] || {};

    return (
      <ProfileCard
        key={alum.id}
        profileId={alum.id}
        profilePictureKey={alum.profilePictureKey}
        name={`${alum.firstNameDisplay} ${alum.lastNameDisplay}`}
        line1={`${cohort.name} ${cohort.year}`}
        line2={branches[alum.branch]}
        line3={alum.headline}
        location={
          alum.cityName
            ? `${alum.cityName}${alum.stateCode ? `, ${alum.stateCode}` : ""}`
            : ""
        }
        cardPath={buildAlumPath(alum.owner)}
      />
    );
  });

  return (
    <>
      {errorMsg && (
        <div>
          <Alert severity="error">{errorMsg}</Alert>
        </div>
      )}
      {allAlumni.length > 0 && (
        <>
          <Sidebar
            displayMobile
            fabIcon={<FilterListRoundedIcon />}
            fabLabel="Filter"
          >
            <div className="side-bar-content">
              <AlumniFilter
                cohorts={cohorts}
                allAlumni={allAlumni}
                setAlumniToDisplay={setAlumniToDisplay}
              />
            </div>
          </Sidebar>
          <Main>
            <div className="alumni">
              <AlumniSearch cohorts={cohorts} allAlumni={allAlumni} />
              <div className="alumni-result">
                {alumniToDisplay.length > 0 && Object.keys(cohorts).length > 0 && (
                  <>
                    <div className="alum-cards-grid">{alumCards}</div>
                    {batchEndIndex < alumniToDisplay.length && (
                      <Button
                        className="load-button"
                        color="primary"
                        variant="contained"
                        onClick={updateBatchEndIndex}
                      >
                        See more alumni
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </Main>
        </>
      )}
    </>
  );
};

Alumni.propTypes = propTypes;

export default Alumni;
